import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../ui/button";
import { ArrowLeft, ChevronDown, Eye, Loader2, Play, Plus, Save, Search, Upload, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { DocumentManager } from "@/components/planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";

interface OtherFinancialAssetsSecurityDepositsProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

type DurationType = "Monthly" | "Quarterly" | "Semi-Annually" | "Annually" | "";
type RepaymentMethod = "End of Period" | "Custom Repayment Schedule" | "";

interface DepositRow {
  id: string;
  name: string;
  deposit_payment_date: string;
  effective_interest_rate: string;
  duration: DurationType;
  amount_of_security: string;
  repayment_method: RepaymentMethod;
  term_of_deposit: string;
  repayments?: Record<string, number>;
}

interface ReconUserRow {
  reason: string;
  amount: number;
}

interface SecurityDepositsReconciliationOutput {
  A?: number;
  B?: number;
  difference?: number;
  user_rows?: ReconUserRow[];
  net_difference?: number;
}

interface ExcelColumn {
  name: string;
  value: string;
}

/** One period row inside Execution_OFA_SDOut.json schedule */
interface SecurityDepositSchedulePeriod {
  sl_no?: number;
  period?: string;
  opening_balance?: number;
  interest_income?: number;
  closing_balance?: number;
  rou?: number;
  depreciation?: number;
  repayment?: number;
}

/** One deposit block inside Execution_OFA_SDOut.json `data` */
interface SecurityDepositScheduleItem {
  name?: string;
  deposit_payment_date?: string;
  effective_interest_rate?: number;
  duration?: string;
  amount_of_security?: number;
  repayment_method?: string;
  term_of_deposit?: number;
  repayments?: Record<string, number>;
  summary?: {
    present_value?: number;
    total_rou?: number;
    period_depreciation?: number;
  };
  schedule?: SecurityDepositSchedulePeriod[];
}

/** Lease row shape from Execution_LEASE_AssessmentInp.json */
interface LeaseAssessmentLease {
  lease_name?: string;
  lease_type?: string;
  commencement_date?: string;
  lease_period?: number;
  int_ror?: number;
  payment_cycle?: string;
  conditions?: Record<string, string>;
  payment_details?: unknown[];
}

const FILE_NAME = "Execution_OFA_SDInp.json";
const LEASE_ASSESSMENT_FILE_NAME = "Execution_LEASE_AssessmentInp.json";

function parseCommencementToIso(raw: string): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (us) {
    const mm = us[1].padStart(2, "0");
    const dd = us[2].padStart(2, "0");
    const yyyy = us[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return "";
}

function mapPaymentCycleToDuration(cycle: string): DurationType {
  const n = String(cycle ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (n === "monthly") return "Monthly";
  if (n === "quarterly") return "Quarterly";
  if (n === "semi-annually" || n === "semi annually") return "Semi-Annually";
  if (n === "annually" || n === "annual") return "Annually";
  const titled = String(cycle ?? "").trim() as DurationType;
  if (["Monthly", "Quarterly", "Semi-Annually", "Annually"].includes(titled)) return titled;
  return "";
}

function formatIsoDateDdMmYyyy(iso: string): string {
  const s = String(iso ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s || "—";
}

function formatInInteger(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n));
}

function formatInterestPercent(rate: number | undefined | null): string {
  if (rate === undefined || rate === null || Number.isNaN(Number(rate))) return "—";
  return `${Number(rate)}%`;
}

const SEC_DEP_SECTION = "Execution_OFA_SecDep";
const SD_SCHEDULE_FILE_NAME = "Execution_OFA_SDOut.json";
const SD_OUT_FILE_NAME = "Execution_OFA_RecTbl.json";
const SD_OUT_SECTION = "Execution_OFA_RecTbl";
const OFA_WORKSPACE_PREFIX = "OFA";
const OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID = "OFA-SD-INFERENCE-CONCLUSION";
const OFA_SECURITY_DEPOSIT_INFERENCE_SECTION_KEY = "execution-substantive:Other Financial Assets";
const OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION =
  "Document the inference and conclusion from the above procedure performed.";
const SEC_DEP_FIELD_KEYS: Array<{ key: string; label: string }> = [
  { key: "date_of_deposit", label: "Date of Deposit" },
  { key: "closing_amount", label: "Closing Amount" },
  { key: "party_name", label: "Party Name" },
  { key: "classification", label: "Classification" },
];

export default function OtherFinancialAssetsSecurityDeposits({
  onBack,
  setActiveSection,
}: OtherFinancialAssetsSecurityDepositsProps) {
  const [rows, setRows] = useState<DepositRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isRunningSchedule, setIsRunningSchedule] = useState(false);

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleViewOpen, setScheduleViewOpen] = useState(false);
  const [scheduleViewLoading, setScheduleViewLoading] = useState(false);
  const [scheduleViewEntry, setScheduleViewEntry] = useState<SecurityDepositScheduleItem | null>(null);
  const [scheduleViewLabel, setScheduleViewLabel] = useState<string>("");
  const [scheduleRowId, setScheduleRowId] = useState<string>("");
  const [scheduleRows, setScheduleRows] = useState<string[]>([]);
  const [secDepFiles, setSecDepFiles] = useState<string[]>([]);
  const [secDepSearchStatus, setSecDepSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [secDepUploadStatus, setSecDepUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [secDepReferenceValue, setSecDepReferenceValue] = useState<string>("");
  const [selectedSecDepFile, setSelectedSecDepFile] = useState<string>("");
  const [secDepExcelColumns, setSecDepExcelColumns] = useState<ExcelColumn[]>([]);
  const [isLoadingSecDepColumns, setIsLoadingSecDepColumns] = useState(false);
  const [secDepColumnMappings, setSecDepColumnMappings] = useState<Record<string, string>>({});
  const [assessmentLeases, setAssessmentLeases] = useState<LeaseAssessmentLease[]>([]);
  const [leaseSelectByRow, setLeaseSelectByRow] = useState<Record<string, string>>({});

  const [isLoadingSdOut, setIsLoadingSdOut] = useState(false);
  const [isRunningSdOut, setIsRunningSdOut] = useState(false);
  const [isSavingSdOut, setIsSavingSdOut] = useState(false);

  const [sdOutA, setSdOutA] = useState<number>(0);
  const [sdOutB, setSdOutB] = useState<number>(0);
  const [sdOutDifference, setSdOutDifference] = useState<number>(0);
  const [sdOutUserRows, setSdOutUserRows] = useState<ReconUserRow[]>([]);
  const [sdOutLoaded, setSdOutLoaded] = useState(false);
  const [sdOutSaveStatus, setSdOutSaveStatus] = useState<"idle" | "saved" | "error">("idle");
  const [sdOutSaveError, setSdOutSaveError] = useState<string | null>(null);

  // Security deposit listing inputs/run section (dropdown)
  const [secDepListingOpen, setSecDepListingOpen] = useState(false);

  // Document inference / conclusion (custom-procedure-style persistence)
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestionResponse, setInferenceQuestionResponse] = useState<{
    textResponse?: { html: string; plainText: string } | null;
    documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
  }>({});
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<"idle" | "saved" | "error">("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  const [inferenceSaving, setInferenceSaving] = useState(false);

  const intervalForDuration = (duration: DurationType): number => {
    switch (duration) {
      case "Monthly":
        return 1;
      case "Quarterly":
        return 3;
      case "Semi-Annually":
        return 6;
      case "Annually":
        return 12;
      default:
        return 1;
    }
  };

  const makeEmptyRow = (): DepositRow => ({
    id: `dep-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    deposit_payment_date: "",
    effective_interest_rate: "",
    duration: "",
    amount_of_security: "",
    repayment_method: "",
    term_of_deposit: "",
    repayments: undefined,
  });

  const normalizeLoadedRow = (entry: any): DepositRow => ({
    id: `dep-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: String(entry?.name ?? ""),
    deposit_payment_date: String(entry?.deposit_payment_date ?? ""),
    effective_interest_rate:
      entry?.effective_interest_rate === null || entry?.effective_interest_rate === undefined
        ? ""
        : String(entry.effective_interest_rate),
    duration: (entry?.duration ?? "") as DurationType,
    amount_of_security:
      entry?.amount_of_security === null || entry?.amount_of_security === undefined
        ? ""
        : String(entry.amount_of_security),
    repayment_method: (entry?.repayment_method ?? "") as RepaymentMethod,
    term_of_deposit:
      entry?.term_of_deposit === null || entry?.term_of_deposit === undefined
        ? ""
        : String(entry.term_of_deposit),
    repayments:
      entry?.repayments && typeof entry.repayments === "object"
        ? Object.fromEntries(
            Object.entries(entry.repayments).map(([k, v]) => [String(k), Number(v)])
          )
        : undefined,
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(FILE_NAME);
        const deposits = result?.success && Array.isArray(result?.data?.deposits) ? result.data.deposits : [];
        setRows(deposits.map((d: any) => normalizeLoadedRow(d)));

        // Persisted Excel mapping state (optional)
        const listingMeta = result?.data?.sec_deposit_listing ?? result?.data?.secDepListing ?? null;
        if (listingMeta && typeof listingMeta === "object") {
          setSelectedSecDepFile(String(listingMeta.selectedSecDepFile ?? listingMeta.selected_file ?? ""));
          setSecDepColumnMappings((prev) => ({
            ...prev,
            ...(typeof listingMeta.columnMappings === "object" && listingMeta.columnMappings ? listingMeta.columnMappings : {}),
          }));
        }
      } catch (error) {
        console.error("Failed to load OFA security deposits input file:", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const loadSdScheduleOutput = useCallback(async (): Promise<SecurityDepositScheduleItem[]> => {
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(SD_SCHEDULE_FILE_NAME);
      return result?.success && Array.isArray(result?.data?.data)
        ? (result.data.data as SecurityDepositScheduleItem[])
        : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadLeaseAssessment = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(LEASE_ASSESSMENT_FILE_NAME);
        if (cancelled) return;
        if (result?.success && result?.data && Array.isArray(result.data.leases)) {
          setAssessmentLeases(result.data.leases as LeaseAssessmentLease[]);
        } else {
          setAssessmentLeases([]);
        }
      } catch {
        if (!cancelled) setAssessmentLeases([]);
      }
    };
    void loadLeaseAssessment();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const loadSdOut = async () => {
      setIsLoadingSdOut(true);
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(SD_OUT_FILE_NAME);
        const data: SecurityDepositsReconciliationOutput = result?.success ? result?.data || {} : {};

        setSdOutA(Number(data.A ?? 0) || 0);
        setSdOutB(Number(data.B ?? 0) || 0);
        setSdOutDifference(Number(data.difference ?? 0) || 0);

        const userRowsRaw = Array.isArray(data.user_rows) ? data.user_rows : [];
        setSdOutUserRows(
          userRowsRaw.map((r: any) => ({
            reason: String(r?.reason ?? ""),
            amount: Number(r?.amount ?? 0) || 0,
          }))
        );
        setSdOutLoaded(true);
      } catch (error) {
        console.error("Failed to load OFA security deposits output:", error);
        setSdOutLoaded(false);
      } finally {
        setIsLoadingSdOut(false);
      }
    };

    loadSdOut();
  }, []);

  // Load company name for persistence payload
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    void loadCompanyName();
  }, []);

  useEffect(() => {
    if (!companyName) return;
    const loadInferenceSaved = async () => {
      try {
        const fileName = `Execution_${OFA_WORKSPACE_PREFIX}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const saved = result.data.substantiveProcedures[OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID];
          if (saved) {
            localStorage.removeItem(`textbox_${OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID}`);

            let textResponse: { html: string; plainText: string } | null = null;
            if (saved.textResponse) {
              textResponse = {
                html: saved.textResponse.html || saved.textResponse.plainText || "",
                plainText:
                  saved.textResponse.plainText ||
                  (saved.textResponse.html ? String(saved.textResponse.html).replace(/<[^>]*>/g, "") : ""),
              };
              if (textResponse.html) {
                localStorage.setItem(`textbox_${OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID}`, textResponse.html);
              }
            }

            const documents = (saved.documents || []).map((docName: string) => ({
              id: `doc_${docName}`,
              type: "linked" as const,
              name: docName,
            }));

            setInferenceQuestionResponse({ textResponse, documents });
            return;
          }
        }
      } catch (e) {
        console.warn("Failed to load saved security deposit inference:", e);
      }
      localStorage.removeItem(`textbox_${OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID}`);
      setInferenceQuestionResponse({});
    };

    void loadInferenceSaved();
  }, [companyName]);

  const sdOutUserRowsSum = useMemo(() => {
    return sdOutUserRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  }, [sdOutUserRows]);

  const sdOutNetDifference = useMemo(() => {
    return (Number(sdOutDifference) || 0) - sdOutUserRowsSum;
  }, [sdOutDifference, sdOutUserRowsSum]);

  const searchForSecDepFiles = async () => {
    setSecDepSearchStatus("searching");
    try {
      if ((window as any).sharePointAPI?.loadComcFiles) {
        const result = await (window as any).sharePointAPI.loadComcFiles(SEC_DEP_SECTION);
        if (result?.success && result?.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setSecDepFiles(files);
            setSecDepSearchStatus("found");
          } else {
            setSecDepSearchStatus("not_found");
          }
        } else {
          setSecDepSearchStatus("not_found");
        }
      } else {
        setSecDepSearchStatus("not_found");
      }
    } catch {
      setSecDepSearchStatus("not_found");
    }
  };

  useEffect(() => {
    searchForSecDepFiles();
  }, []);

  const uploadSecDepFile = async () => {
    setSecDepUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;

        try {
          const base64Content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              resolve(result.split(",")[1]);
            };
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsDataURL(file);
          });

          const result = await (window as any).sharePointAPI?.uploadFile?.({
            fileContent: base64Content,
            fileName: file.name,
            referenceValue: secDepReferenceValue,
            folderName: "client",
            fyYear: getFY_YEAR(),
            sectionList: [SEC_DEP_SECTION],
          });

          if (result?.success) {
            setSelectedSecDepFile(file.name);
            setSecDepFiles([file.name]);
            setSecDepSearchStatus("found");
            setSecDepUploadStatus("completed");
          } else {
            setSecDepUploadStatus("error");
          }
        } catch {
          setSecDepUploadStatus("error");
        }
      };
      input.click();
    } catch {
      setSecDepUploadStatus("error");
    }
  };

  const loadSecDepExcelColumns = async () => {
    if (!selectedSecDepFile) return;
    setIsLoadingSecDepColumns(true);
    try {
      if ((window as any).payroll?.loadExcelColumns) {
        const result = await (window as any).payroll.loadExcelColumns(selectedSecDepFile);
        if (result?.ok && result?.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setSecDepExcelColumns(columns);
          return;
        }
      }
      setSecDepExcelColumns([]);
    } catch {
      setSecDepExcelColumns([]);
    } finally {
      setIsLoadingSecDepColumns(false);
    }
  };

  const applyLeaseToRow = useCallback((rowId: string, lease: LeaseAssessmentLease) => {
    const name = String(lease.lease_name ?? "").trim();
    const deposit_payment_date = parseCommencementToIso(String(lease.commencement_date ?? ""));
    const effective_interest_rate =
      lease.int_ror === null || lease.int_ror === undefined ? "" : String(lease.int_ror);
    const duration = mapPaymentCycleToDuration(String(lease.payment_cycle ?? ""));
    const term_of_deposit =
      lease.lease_period === null || lease.lease_period === undefined
        ? ""
        : String(lease.lease_period);

    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              name,
              deposit_payment_date,
              effective_interest_rate,
              duration,
              term_of_deposit,
            }
          : row
      )
    );
  }, []);

  const updateRow = (id: string, patch: Partial<DepositRow>) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const next = { ...row, ...patch };
        if (patch.repayment_method && patch.repayment_method !== "Custom Repayment Schedule") {
          next.repayments = undefined;
        }
        return next;
      })
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  const handleRemoveRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const openCustomScheduleDialog = (row: DepositRow) => {
    const term = Number(row.term_of_deposit || 0);
    const base = Number.isFinite(term) && term > 0 ? term : 0;
    const existing = row.repayments || {};

    const nextRows = Array.from({ length: base }, (_, idx) => {
      const installmentKey = String(idx + 1);
      return existing[installmentKey] !== undefined ? String(existing[installmentKey]) : "";
    });

    setScheduleRowId(row.id);
    setScheduleRows(nextRows);
    setScheduleOpen(true);
  };

  const saveCustomSchedule = () => {
    const target = rows.find((r) => r.id === scheduleRowId);
    if (!target) {
      setScheduleOpen(false);
      return;
    }

    const repayments: Record<string, number> = {};
    scheduleRows.forEach((value, idx) => {
      const trimmed = String(value ?? "").trim();
      if (trimmed === "") return;
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) return;
      const installmentKey = String(idx + 1);
      repayments[installmentKey] = parsed;
    });

    updateRow(scheduleRowId, { repayments });
    setScheduleOpen(false);
  };

  const payload = useMemo(() => {
    const normalizeRepaymentKeys = (
      repayments: Record<string, number>,
      duration: DurationType,
      term: number
    ): Record<string, number> => {
      const interval = intervalForDuration(duration);
      const normalized: Record<string, number> = {};

      Object.entries(repayments).forEach(([rawKey, value]) => {
        const keyNum = Number(rawKey);
        const amountNum = Number(value);
        if (!Number.isFinite(keyNum) || !Number.isFinite(amountNum)) return;

        // Preferred: installment-number keys (1..term)
        if (keyNum >= 1 && keyNum <= term) {
          normalized[String(keyNum)] = amountNum;
          return;
        }

        // Backward-compat for previously multiplied keys (e.g. 12, 96, 216 for Annually)
        if (interval > 1 && keyNum % interval === 0) {
          const installment = keyNum / interval;
          if (installment >= 1 && installment <= term) {
            normalized[String(installment)] = amountNum;
          }
        }
      });

      return normalized;
    };

    const deposits = rows.map((row) => {
      const parsedTerm = row.term_of_deposit === "" ? 0 : Number(row.term_of_deposit);
      const base: any = {
        name: row.name,
        deposit_payment_date: row.deposit_payment_date,
        effective_interest_rate: row.effective_interest_rate === "" ? 0 : Number(row.effective_interest_rate),
        duration: row.duration,
        amount_of_security: row.amount_of_security === "" ? 0 : Number(row.amount_of_security),
        repayment_method: row.repayment_method,
        term_of_deposit: parsedTerm,
      };
      if (
        row.repayment_method === "Custom Repayment Schedule" &&
        row.repayments &&
        Object.keys(row.repayments).length > 0
      ) {
        base.repayments = normalizeRepaymentKeys(row.repayments, row.duration, parsedTerm);
      }
      return base;
    });
    return { deposits };
  }, [rows]);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus("idle");
    setSaveError(null);
    try {
      const uploadPayload = {
        ...payload,
        sec_deposit_listing: {
          selectedSecDepFile,
          columnMappings: secDepColumnMappings,
        },
      };

      const jsonString = JSON.stringify(uploadPayload, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: FILE_NAME,
        referenceValue: "Execution OFA Security Deposits Input",
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: ["Execution_OFA_SDInp"],
      });

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || "Failed to save Security Deposits input");
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2500);
    } catch (error: any) {
      console.error("Failed to save OFA security deposits input:", error);
      setSaveStatus("error");
      setSaveError(error?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const openScheduleViewDialog = async (row: DepositRow) => {
    setScheduleViewLabel(row.name.trim() || "—");
    setScheduleViewOpen(true);
    setScheduleViewLoading(true);
    setScheduleViewEntry(null);
    try {
      const items = await loadSdScheduleOutput();
      const key = row.name.trim();
      const entry =
        items.find((d) => String(d.name ?? "").trim() === key) ??
        (key === "" ? undefined : items.find((d) => String(d.name ?? "").trim().toLowerCase() === key.toLowerCase())) ??
        null;
      setScheduleViewEntry(entry);
    } finally {
      setScheduleViewLoading(false);
    }
  };

  const runSecurityDepositSchedule = async () => {
    setIsRunningSchedule(true);
    try {
      const result = await window.payroll?.run?.("execute_ofa_security_deposit_schedule", {
        inputFiles: [],
        options: {
          fy_year: getFY_YEAR(),
        },
      });
      if (!result?.ok) {
        throw new Error(result?.error || "Failed to run schedule generation.");
      }
      alert("Security deposit schedule generated and uploaded to SharePoint.");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Run failed.";
      console.error("Security deposit schedule run failed:", error);
      alert(message);
    } finally {
      setIsRunningSchedule(false);
    }
  };

  const addSdOutUserRow = () => {
    setSdOutUserRows((prev) => [...prev, { reason: "", amount: 0 }]);
  };

  const removeSdOutUserRow = (index: number) => {
    setSdOutUserRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSdOutUserRow = (index: number, patch: Partial<ReconUserRow>) => {
    setSdOutUserRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch, amount: patch.amount ?? r.amount } : r))
    );
  };

  const loadSdOutFromSharePoint = async () => {
    setIsLoadingSdOut(true);
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(SD_OUT_FILE_NAME);
      const data: SecurityDepositsReconciliationOutput = result?.success ? result?.data || {} : {};

      setSdOutA(Number(data.A ?? 0) || 0);
      setSdOutB(Number(data.B ?? 0) || 0);
      setSdOutDifference(Number(data.difference ?? 0) || 0);

      const userRowsRaw = Array.isArray(data.user_rows) ? data.user_rows : [];
      setSdOutUserRows(
        userRowsRaw.map((r: any) => ({
          reason: String(r?.reason ?? ""),
          amount: Number(r?.amount ?? 0) || 0,
        }))
      );

      setSdOutLoaded(true);
    } catch (error) {
      console.error("Failed to reload OFA security deposits output:", error);
    } finally {
      setIsLoadingSdOut(false);
    }
  };

  const runSdOutReconciliation = async () => {
    if (!selectedSecDepFile) {
      alert("Please select the Security Deposit Listing Excel file first.");
      return;
    }

    const closingAmountColumn = secDepColumnMappings["closing_amount"];
    if (!closingAmountColumn) {
      alert('Please map the "Closing Amount" column first.');
      return;
    }

    setIsRunningSdOut(true);
    setSdOutSaveStatus("idle");
    setSdOutSaveError(null);

    try {
      const result = await window.payroll?.run?.("execute_ofa_security_deposits_reconciliation", {
        inputFiles: [],
        options: {
          excel_file_name: selectedSecDepFile,
          column_name: closingAmountColumn,
        },
      });

      if (!result?.ok) {
        throw new Error(result?.error || "Failed to run reconciliation.");
      }

      await loadSdOutFromSharePoint();
      alert("Security Deposit Listing reconciliation completed.");
    } catch (error: any) {
      console.error("Run reconciliation failed:", error);
      alert(error?.message || "Run reconciliation failed.");
    } finally {
      setIsRunningSdOut(false);
    }
  };

  const saveSdOutReconciliation = async () => {
    setIsSavingSdOut(true);
    setSdOutSaveStatus("idle");
    setSdOutSaveError(null);
    try {
      const output: SecurityDepositsReconciliationOutput = {
        A: sdOutA,
        B: sdOutB,
        difference: sdOutDifference,
        user_rows: sdOutUserRows.map((r) => ({
          reason: r.reason,
          amount: Number(r.amount) || 0,
        })),
        net_difference: sdOutNetDifference,
      };

      const jsonString = JSON.stringify(output, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: SD_OUT_FILE_NAME,
        referenceValue: "Execution OFA Security Deposits Reconciliation",
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [SD_OUT_SECTION],
      });

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || "Failed to save reconciliation output");
      }

      setSdOutSaveStatus("saved");
      setTimeout(() => setSdOutSaveStatus("idle"), 2500);
    } catch (error: any) {
      console.error("Save reconciliation failed:", error);
      setSdOutSaveStatus("error");
      setSdOutSaveError(error?.message || "Failed to save reconciliation");
    } finally {
      setIsSavingSdOut(false);
    }
  };

  const saveInferenceConclusion = useCallback(async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus("error");
      return;
    }
    setInferenceSaving(true);
    setInferenceSaveStatus("idle");
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content before processing
      const documentManagers = document.querySelectorAll("[data-document-manager]");
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute("data-item-id");
        if (!itemId) continue;
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === "function") {
          autoSaveMethod();
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));

      const phaseFormatted = "Execution";
      const fileName = `${phaseFormatted}_${OFA_WORKSPACE_PREFIX}_Responses.json`;

      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch {
        /* start fresh */
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }
      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const textboxFromLocalStorage = localStorage.getItem(
        `textbox_${OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID}`
      );
      const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;

      const sectionToFind = `${phaseFormatted}_${OFA_WORKSPACE_PREFIX}_${OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID}`;
      let documentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            documentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (e) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, e);
      }

      let textResponse: { html: string; plainText: string } = { html: "", plainText: "" };
      if (hasTextboxContent) {
        textResponse = {
          html: textboxFromLocalStorage as string,
          plainText: (textboxFromLocalStorage as string).replace(/<[^>]*>/g, ""),
        };
      } else if (inferenceQuestionResponse?.textResponse) {
        textResponse = inferenceQuestionResponse.textResponse;
      }

      existingData.substantiveProcedures[OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID] = {
        selectedOption: "",
        textResponse,
        documents: documentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `${phaseFormatted} ${OFA_WORKSPACE_PREFIX} Substantive Responses`,
        folderName: "juggernaut",
        fyYear,
        sectionList: [`${phaseFormatted}_${OFA_WORKSPACE_PREFIX}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference and conclusion");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (error) {
      setInferenceSaveStatus("error");
      setInferenceSaveError(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setInferenceSaving(false);
    }
  }, [companyName, inferenceQuestionResponse]);

  const handleBack = () => {
    if (setActiveSection) {
      setActiveSection("ofa-substantive");
      return;
    }
    onBack?.();
  };

  if (loading) {
    return (
      <div className="flex min-h-[280px] items-center justify-center">
        <div className="text-white/60">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Security Deposits</h2>
          <p className="text-sm text-white/60">Reconciliation</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#4da3ff] text-black hover:bg-[#4da3ff]/90"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
          <Button
            onClick={runSecurityDepositSchedule}
            disabled={isRunningSchedule}
            className="bg-emerald-600 text-white hover:bg-emerald-600/90"
          >
            {isRunningSchedule ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            Run
          </Button>
          <Button variant="outline" onClick={handleBack} className="border-white/20 bg-white/5 text-white hover:bg-white/10">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-white">Reconciliation</CardTitle>
            <Button
              onClick={handleAddRow}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add row
            </Button>
          </div>
          {saveStatus !== "idle" && (
            <div
              className={[
                "rounded-md border px-3 py-2 text-sm",
                saveStatus === "saved"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-red-500/30 bg-red-500/10 text-red-200",
              ].join(" ")}
            >
              {saveStatus === "saved" ? "Saved successfully." : saveError || "Save failed."}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Deposit Payment Date</TableHead>
                <TableHead>Effective Interest Rate</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Amount of Security Deposit</TableHead>
                <TableHead>Repayment Method</TableHead>
                <TableHead>Term of Deposit (months)</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-6 text-white/60">
                    No rows yet. Click "Add row" to start.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="min-w-[200px]">
                      <div className="flex flex-col gap-2">
                        {assessmentLeases.length > 0 && (
                          <Select
                            value={leaseSelectByRow[row.id] ?? "__none__"}
                            onValueChange={(v) => {
                              if (v === "__none__") {
                                setLeaseSelectByRow((s) => ({ ...s, [row.id]: "__none__" }));
                                return;
                              }
                              const idx = Number(v);
                              const lease = assessmentLeases[idx];
                              if (lease) applyLeaseToRow(row.id, lease);
                              setLeaseSelectByRow((s) => ({ ...s, [row.id]: "__none__" }));
                            }}
                            searchable={false}
                          >
                            <SelectTrigger className="bg-black/30 border-white/10 text-white">
                              <SelectValue placeholder="Fill from lease…" />
                            </SelectTrigger>
                            <SelectContent searchable={false}>
                              <SelectItem value="__none__">— Select lease —</SelectItem>
                              {assessmentLeases.map((lease, idx) => (
                                <SelectItem key={idx} value={String(idx)}>
                                  {String(lease.lease_name ?? "").trim() || `Lease ${idx + 1}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Input
                          value={row.name}
                          onChange={(e) => updateRow(row.id, { name: e.target.value })}
                          placeholder={
                            assessmentLeases.length ? "Lease name or type new" : "Entered by user"
                          }
                          className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[170px]">
                      <Input
                        type="date"
                        value={row.deposit_payment_date}
                        onChange={(e) => updateRow(row.id, { deposit_payment_date: e.target.value })}
                        className="bg-black/30 border-white/10 text-white"
                      />
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      <Input
                        value={row.effective_interest_rate}
                        onChange={(e) => updateRow(row.id, { effective_interest_rate: e.target.value })}
                        placeholder="Entered by user"
                        className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                      />
                    </TableCell>
                    <TableCell className="min-w-[190px]">
                      <Select
                        value={row.duration || "__none__"}
                        onValueChange={(v) => updateRow(row.id, { duration: v === "__none__" ? "" : (v as DurationType) })}
                        searchable={false}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent searchable={false}>
                          <SelectItem value="__none__">Select</SelectItem>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Quarterly">Quarterly</SelectItem>
                          <SelectItem value="Semi-Annually">Semi-Annually</SelectItem>
                          <SelectItem value="Annually">Annually</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="min-w-[210px]">
                      <Input
                        value={row.amount_of_security}
                        onChange={(e) => updateRow(row.id, { amount_of_security: e.target.value })}
                        placeholder="Entered by user"
                        className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                      />
                    </TableCell>
                    <TableCell className="min-w-[260px]">
                      <div className="flex items-center gap-2">
                        <Select
                          value={row.repayment_method || "__none__"}
                          onValueChange={(v) =>
                            updateRow(row.id, {
                              repayment_method: v === "__none__" ? "" : (v as RepaymentMethod),
                            })
                          }
                          searchable={false}
                        >
                          <SelectTrigger className="min-w-[170px]">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent searchable={false}>
                            <SelectItem value="__none__">Select</SelectItem>
                            <SelectItem value="End of Period">End of Period</SelectItem>
                            <SelectItem value="Custom Repayment Schedule">Custom Repayment Schedule</SelectItem>
                          </SelectContent>
                        </Select>
                        {row.repayment_method === "Custom Repayment Schedule" && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-white/20 text-white hover:bg-white/10"
                            onClick={() => openCustomScheduleDialog(row)}
                            disabled={!row.term_of_deposit || Number(row.term_of_deposit) <= 0}
                          >
                            Open
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[190px]">
                      <Input
                        value={row.term_of_deposit}
                        onChange={(e) => updateRow(row.id, { term_of_deposit: e.target.value })}
                        placeholder="Entered by user"
                        className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          type="button"
                          onClick={() => void openScheduleViewDialog(row)}
                          variant="ghost"
                          size="sm"
                          className="text-sky-400 hover:text-sky-300"
                          title="View schedule from Execution_OFA_SDOut.json"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleRemoveRow(row.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          title="Remove row"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={scheduleViewOpen}
        onOpenChange={(open) => {
          setScheduleViewOpen(open);
          if (!open) {
            setScheduleViewEntry(null);
            setScheduleViewLabel("");
            setScheduleViewLoading(false);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] w-[min(96vw,1200px)] max-w-[1200px] overflow-y-auto bg-black/95 border-white/10 text-white sm:max-w-[1200px]">
          <DialogHeader>
            <DialogTitle>Security deposit schedule</DialogTitle>
            <DialogDescription className="text-white/60">
              {scheduleViewLabel ? `Name: ${scheduleViewLabel}` : "From Execution_OFA_SDOut.json"}
            </DialogDescription>
          </DialogHeader>

          {scheduleViewLoading ? (
            <div className="flex min-h-[120px] items-center justify-center gap-2 text-white/70">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading schedule…
            </div>
          ) : !scheduleViewEntry ? (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
              No schedule found in <span className="font-mono">Execution_OFA_SDOut.json</span> for this row. Use{" "}
              <span className="font-semibold">Run</span> after saving inputs, and ensure the <span className="font-semibold">Name</span>{" "}
              matches the lease name in the output file.
            </p>
          ) : (
            <div className="space-y-6">
              <div className="overflow-x-auto rounded-md border border-white/20">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/20 hover:bg-transparent">
                      <TableHead className="border border-white/20 text-white">Name</TableHead>
                      <TableHead className="border border-white/20 text-white">Deposit Payment Date</TableHead>
                      <TableHead className="border border-white/20 text-white">Effective Interest Rate</TableHead>
                      <TableHead className="border border-white/20 text-white">Duration</TableHead>
                      <TableHead className="border border-white/20 text-white">Amount of Secur.</TableHead>
                      <TableHead className="border border-white/20 text-white">Repayment Method</TableHead>
                      <TableHead className="border border-white/20 text-white">Term of Deposit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="border-white/20 hover:bg-white/5">
                      <TableCell className="border border-white/20 text-white">
                        {scheduleViewEntry.name ?? "—"}
                      </TableCell>
                      <TableCell className="border border-white/20 text-white">
                        {formatIsoDateDdMmYyyy(String(scheduleViewEntry.deposit_payment_date ?? ""))}
                      </TableCell>
                      <TableCell className="border border-white/20 text-white">
                        {formatInterestPercent(scheduleViewEntry.effective_interest_rate)}
                      </TableCell>
                      <TableCell className="border border-white/20 text-white">
                        {scheduleViewEntry.duration ?? "—"}
                      </TableCell>
                      <TableCell className="border border-white/20 text-white">
                        {formatInInteger(scheduleViewEntry.amount_of_security)}
                      </TableCell>
                      <TableCell className="border border-white/20 text-white">
                        {scheduleViewEntry.repayment_method ?? "—"}
                      </TableCell>
                      <TableCell className="border border-white/20 text-white">
                        {scheduleViewEntry.term_of_deposit !== undefined && scheduleViewEntry.term_of_deposit !== null
                          ? String(scheduleViewEntry.term_of_deposit)
                          : "—"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              <div className="overflow-x-auto rounded-md border border-white/20">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/20 hover:bg-transparent">
                      <TableHead className="border border-white/20 text-white">Sl. No.</TableHead>
                      <TableHead className="border border-white/20 text-white">Month</TableHead>
                      <TableHead className="border border-white/20 text-white">Opening Balance</TableHead>
                      <TableHead className="border border-white/20 text-white">Interest Income</TableHead>
                      <TableHead className="border border-white/20 text-white">Closing Balance</TableHead>
                      <TableHead className="border border-white/20 text-white">ROU</TableHead>
                      <TableHead className="border border-white/20 text-white">Depreciation</TableHead>
                      {(scheduleViewEntry.schedule ?? []).some((r) => r.repayment != null && r.repayment !== 0) && (
                        <TableHead className="border border-white/20 text-white">Repayment</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(scheduleViewEntry.schedule ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={
                            7 +
                            ((scheduleViewEntry.schedule ?? []).some(
                              (r) => r.repayment != null && r.repayment !== 0
                            )
                              ? 1
                              : 0)
                          }
                          className="border border-white/20 py-4 text-center text-white/60"
                        >
                          No schedule rows in file.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (scheduleViewEntry.schedule ?? []).map((line, idx) => (
                        <TableRow key={line.sl_no ?? idx} className="border-white/20 hover:bg-white/5">
                          <TableCell className="border border-white/20 text-white">
                            {line.sl_no ?? idx + 1}
                          </TableCell>
                          <TableCell className="border border-white/20 text-white">
                            {line.period ?? "—"}
                          </TableCell>
                          <TableCell className="border border-white/20 text-white">
                            {formatInInteger(line.opening_balance)}
                          </TableCell>
                          <TableCell className="border border-white/20 text-white">
                            {formatInInteger(line.interest_income)}
                          </TableCell>
                          <TableCell className="border border-white/20 text-white">
                            {formatInInteger(line.closing_balance)}
                          </TableCell>
                          <TableCell className="border border-white/20 text-white">
                            {formatInInteger(line.rou)}
                          </TableCell>
                          <TableCell className="border border-white/20 text-white">
                            {formatInInteger(line.depreciation)}
                          </TableCell>
                          {(scheduleViewEntry.schedule ?? []).some(
                            (r) => r.repayment != null && r.repayment !== 0
                          ) && (
                            <TableCell className="border border-white/20 text-white">
                              {line.repayment != null && line.repayment !== 0
                                ? formatInInteger(line.repayment)
                                : "—"}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => setScheduleViewOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="sm:max-w-[760px] max-h-[80vh] overflow-y-auto bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Custom Repayment Schedule</DialogTitle>
            <DialogDescription className="text-white/60">
              Rows are directly based on Term of Deposit.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {scheduleRows.length === 0 ? (
              <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/60">
                Enter "Term of Deposit (months)" first to generate rows.
              </div>
            ) : (
              scheduleRows.map((value, idx) => (
                <div key={idx} className="grid grid-cols-[100px_1fr] items-center gap-3">
                  <div className="text-sm text-white/70">{idx + 1}</div>
                  <Input
                    value={value}
                    onChange={(e) =>
                      setScheduleRows((prev) => prev.map((p, i) => (i === idx ? e.target.value : p)))
                    }
                    placeholder="Amount"
                    className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => setScheduleOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveCustomSchedule}
              className="bg-[#4da3ff] text-black hover:bg-[#4da3ff]/90"
            >
              Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div
            className="w-full cursor-pointer select-none rounded-lg border border-white/15 bg-white/5 px-4 py-3 hover:bg-white/10 transition-colors"
            onClick={() => setSecDepListingOpen((prev) => !prev)}
            role="button"
            aria-expanded={secDepListingOpen}
          >
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-white text-base">Security Deposit Listing Inputs</CardTitle>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-sm">{secDepListingOpen ? "Collapse" : "Expand"}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${secDepListingOpen ? "rotate-180" : ""}`} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {secDepListingOpen && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Search className="h-4 w-4 text-blue-400" />
              <span className="text-sm text-white/80">Search for Existing Files</span>
              {secDepSearchStatus === "found" && <span className="text-green-400 font-semibold">FOUND!</span>}
              {secDepSearchStatus === "not_found" && <span className="text-yellow-400">No files found</span>}
              {secDepSearchStatus === "searching" && <Loader2 className="h-4 w-4 animate-spin text-blue-400" />}
            </div>

            {secDepSearchStatus === "found" && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Select File</Label>
                  <Select value={selectedSecDepFile} onValueChange={setSelectedSecDepFile}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select file" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-black/90 text-white">
                      {secDepFiles.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedSecDepFile && (
                  <Button
                    onClick={loadSecDepExcelColumns}
                    disabled={!selectedSecDepFile || isLoadingSecDepColumns}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isLoadingSecDepColumns ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading Columns...
                      </>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                )}
              </div>
            )}

            {secDepSearchStatus === "not_found" && (
              <div className="space-y-3 rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-4">
                <div className="text-yellow-300 text-sm">File not found. Please upload Security Deposit Listing.</div>
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={secDepReferenceValue}
                    onChange={(e) => setSecDepReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button
                  onClick={uploadSecDepFile}
                  disabled={secDepUploadStatus === "uploading"}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {secDepUploadStatus === "uploading" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload File
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
          )}

          {secDepListingOpen && (
            <>
              <div className="space-y-3">
                <div className="text-sm text-white/80">Map Excel Columns</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {SEC_DEP_FIELD_KEYS.map((field) => (
                    <div key={field.key} className="space-y-2">
                      <Label className="text-white">
                        {field.label}
                        {field.key === "classification" ? " (optional)" : ""}
                      </Label>
                      <Select
                        value={secDepColumnMappings[field.key] || ""}
                        onValueChange={(v) =>
                          setSecDepColumnMappings((prev) => ({ ...prev, [field.key]: v }))
                        }
                        disabled={secDepExcelColumns.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              secDepExcelColumns.length === 0 ? "Load columns first" : "Select column"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="border-white/10 bg-black/90 text-white">
                          {secDepExcelColumns.map((col) => (
                            <SelectItem key={col.value} value={col.value}>
                              {col.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={runSdOutReconciliation}
                    disabled={isRunningSdOut}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isRunningSdOut ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Running...
                      </>
                    ) : (
                      "Run"
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}

            {(sdOutSaveStatus !== "idle" || sdOutSaveError) && (
              <div
                className={[
                  "rounded-md border px-3 py-2 text-sm",
                  sdOutSaveStatus === "saved"
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                    : sdOutSaveStatus === "error"
                    ? "border-red-500/30 bg-red-500/10 text-red-200"
                    : "border-white/10 bg-white/5 text-white/80",
                ].join(" ")}
              >
                {sdOutSaveStatus === "saved"
                  ? "Reconciliation saved."
                  : sdOutSaveError || "Save failed."}
              </div>
            )}

            {/* Reconciliation Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-white/10">
                <thead>
                  <tr className="bg-black/60">
                    <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">
                      Particulars
                    </th>
                    <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                      Amount
                    </th>
                    <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">
                      Amount of Security Deposit as per Working [A]
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white">
                      {sdOutA.toLocaleString("en-IN")}
                    </td>
                    <td className="border border-white/10 px-4 py-2" />
                  </tr>
                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">
                      Amount of Security Deposit as per Financials [B]
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white">
                      {sdOutB.toLocaleString("en-IN")}
                    </td>
                    <td className="border border-white/10 px-4 py-2" />
                  </tr>
                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white font-semibold">
                      Difference [A-B]
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                      {sdOutDifference.toLocaleString("en-IN")}
                    </td>
                    <td className="border border-white/10 px-4 py-2" />
                  </tr>

                  {sdOutUserRows.length === 0 && (
                    <tr className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">
                        User to add rows to reconcile
                      </td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white">
                        —
                      </td>
                      <td className="border border-white/10 px-4 py-2" />
                    </tr>
                  )}

                  {sdOutUserRows.map((row, idx) => (
                    <tr key={`sdout_user_${idx}`} className="hover:bg-white/5">
                      <td className="border border-white/10 px-4 py-2 text-white">
                        <Input
                          value={row.reason}
                          onChange={(e) => updateSdOutUserRow(idx, { reason: e.target.value })}
                          placeholder="Enter Particulars"
                          className="bg-black/30 border-white/10 text-white placeholder:text-white/40"
                        />
                      </td>
                      <td className="border border-white/10 px-4 py-2 text-right text-white">
                        <Input
                          type="number"
                          value={row.amount}
                          onChange={(e) => updateSdOutUserRow(idx, { amount: parseFloat(e.target.value) || 0 })}
                          className="bg-black/30 border-white/10 text-white text-right"
                        />
                      </td>
                      <td className="border border-white/10 px-4 py-2 text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSdOutUserRow(idx)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}

                  <tr className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white font-semibold">
                      Net Difference
                    </td>
                    <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                      {sdOutNetDifference.toLocaleString("en-IN")}
                    </td>
                    <td className="border border-white/10 px-4 py-2" />
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex justify-start items-center gap-3 flex-wrap">
              <Button
                onClick={addSdOutUserRow}
                size="sm"
                variant="outline"
                disabled={!sdOutLoaded}
                className="border-white/10 text-white hover:bg-white/10"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add row
              </Button>

              <Button
                onClick={saveSdOutReconciliation}
                disabled={isSavingSdOut || !sdOutLoaded}
                size="sm"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Reconciliation
              </Button>
            </div>
        </CardContent>
      </Card>

      {/* Document the inference / conclusion (end of procedure) */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-white">{OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION}</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
              {inferenceSaveStatus === "error" && inferenceSaveError && <span className="text-sm text-red-400">Error</span>}
              <Button
                type="button"
                onClick={saveInferenceConclusion}
                disabled={inferenceSaving || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey={OFA_SECURITY_DEPOSIT_INFERENCE_SECTION_KEY}
            groupKey="custom-procedure"
            itemId={OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION_ID}
            itemQuestion={OFA_SECURITY_DEPOSIT_INFERENCE_QUESTION}
            itemTextResponse={inferenceQuestionResponse.textResponse ?? null}
            itemDocuments={inferenceQuestionResponse.documents || []}
            onDocumentsChange={(documents) =>
              setInferenceQuestionResponse((prev) => ({ ...prev, documents }))
            }
            onTextResponseChange={(textResponse) =>
              setInferenceQuestionResponse((prev) => ({ ...prev, textResponse }))
            }
            disabled={false}
          />

          {inferenceSaveError && inferenceSaveStatus === "error" && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
              {inferenceSaveError}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

